import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { OrganizationTypes } from '@/common/constants';

import { useCurrentOrganisationStore } from '../store';

import useCurrentOrganization from './useCurrentOrganization';
import { QueryKeys } from './useHistory';

const useCurrentOrganizationPathChecker = () => {
  const history = useHistory();
  const location = useLocation();

  const {
    observingEmployerId,
    observingPartnerId,
    observingDistributorId,
    observingSubsidiaryId,

    handleSetObservingDistributorId,
    handleSetObservingEmployerId,
    handleSetObservingPartnerId,
    handleSetObservingSubsidiaryId,
  } = useCurrentOrganisationStore();
  const { observingDefault } = useCurrentOrganization();
  const currentPartnerId = useMemo(() => {
    if (observingDefault.type === OrganizationTypes.partner) {
      return observingDefault.id;
    }
    return null;
  }, [observingDefault.id, observingDefault.type]);

  const currentEmployerId = useMemo(() => {
    if (
      observingDefault.type === OrganizationTypes.company ||
      observingDefault.type === OrganizationTypes.employer
    ) {
      return observingDefault.id;
    }
    return null;
  }, [observingDefault.id, observingDefault.type]);
  const currentDistributorId = useMemo(() => {
    if (observingDefault.type === OrganizationTypes.distributor) {
      return observingDefault.id;
    }
    return null;
  }, [observingDefault.id, observingDefault.type]);
  const currentSubsidiaryId = useMemo(() => {
    if (observingDefault.type === OrganizationTypes.subsidiary) {
      return observingDefault.id;
    }
    return null;
  }, [observingDefault.id, observingDefault.type]);
  const ids = useMemo(
    () => [
      observingPartnerId || currentPartnerId,
      observingDistributorId || currentDistributorId,
      observingEmployerId || currentEmployerId,
      observingSubsidiaryId || currentSubsidiaryId,
    ],
    [
      observingDistributorId,
      observingEmployerId,
      observingPartnerId,
      observingSubsidiaryId,
      currentPartnerId,
      currentEmployerId,
      currentDistributorId,
      currentSubsidiaryId,
    ]
  );
  useEffect(() => {
    const len = ids.length;

    const isSomethingBroken = ids.some((item, index) => {
      if (index !== len - 1) {
        if (!item && !!ids[index + 1]) {
          return true;
        }
      }
      return false;
    });
    if (isSomethingBroken) {
      const lastIndex = ids.findIndex((item, index) => {
        if (index !== len - 1) {
          if (!!item && !ids[index + 1]) {
            return true;
          }
        }
        return false;
      });
      const query = new URLSearchParams(location.search);
      if (lastIndex === -1) {
        query.delete(QueryKeys.OBSERVING_PARTNER_QUERY_KEY);
        query.delete(QueryKeys.OBSERVING_DISTRIBUTOR_QUERY_KEY);
        query.delete(QueryKeys.OBSERVING_EMPLOYER_QUERY_KEY);
        query.delete(QueryKeys.OBSERVING_SUBSIDIARY_QUERY_KEY);
      } else if (lastIndex === 0) {
        query.delete(QueryKeys.OBSERVING_DISTRIBUTOR_QUERY_KEY);
        query.delete(QueryKeys.OBSERVING_EMPLOYER_QUERY_KEY);
        query.delete(QueryKeys.OBSERVING_SUBSIDIARY_QUERY_KEY);
      } else if (lastIndex === 1) {
        query.delete(QueryKeys.OBSERVING_EMPLOYER_QUERY_KEY);
        query.delete(QueryKeys.OBSERVING_SUBSIDIARY_QUERY_KEY);
      } else if (lastIndex === 2) {
        query.delete(QueryKeys.OBSERVING_SUBSIDIARY_QUERY_KEY);
      }
      history.replace({
        search: query.toString(),
      });
      if (lastIndex === -1) {
        handleSetObservingPartnerId(null);
        handleSetObservingDistributorId(null);
        handleSetObservingEmployerId(null);
        handleSetObservingSubsidiaryId(null);
      } else if (lastIndex === 0) {
        handleSetObservingDistributorId(null);
        handleSetObservingEmployerId(null);
        handleSetObservingSubsidiaryId(null);
      } else if (lastIndex === 1) {
        handleSetObservingEmployerId(null);
        handleSetObservingSubsidiaryId(null);
      } else if (lastIndex === 2) {
        handleSetObservingSubsidiaryId(null);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);
};

export default useCurrentOrganizationPathChecker;
